import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  startIcon?: ReactNode
  endIcon?: ReactNode
  /** للحقول الرقمية/الباركود: يفرض اتجاه LTR داخل واجهة RTL */
  ltr?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, error, hint, startIcon, endIcon, ltr, id, ...props },
  ref,
) {
  const autoId = useId()
  const inputId = id ?? autoId
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
          {props.required && <span className="text-danger ms-1" aria-hidden>*</span>}
        </label>
      )}

      <div className="relative">
        {startIcon && (
          <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-fg-subtle">
            {startIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          dir={ltr ? 'ltr' : undefined}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-10 w-full rounded-lg border bg-surface px-3 text-sm text-fg',
            'placeholder:text-fg-subtle',
            'transition-[border-color,box-shadow] duration-150',
            'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
            'disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-bg-subtle',
            startIcon && 'ps-10',
            endIcon && 'pe-10',
            ltr && 'text-start',
            error ? 'border-danger focus:border-danger focus:ring-danger/20' : 'border-border',
            className,
          )}
          {...props}
        />

        {endIcon && (
          <span className="absolute inset-y-0 end-0 flex items-center pe-3 text-fg-subtle">{endIcon}</span>
        )}
      </div>

      {error ? (
        <p id={`${inputId}-error`} role="alert" className="mt-1.5 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  )
})
